import { useEffect, useMemo } from 'react';
import { useRequest } from 'ahooks';
import { useLayout } from '~hooks';
import { useTransshipment } from '~contexts/TranshipmentContext';
import { Representative } from '~services';
import {
  Title,
  UsingCellphone,
  Footer,
  ButtonWithArrowRight,
  Form,
} from '~components';
import TakeAuthorization from './sections/TakeAuthorization';
import validationSchema from './validation';
import { makeOnboardingNcaTrack } from '~services/tracking/onboarding-nca';
import { makeRepresentativeTrackingSDK } from '~services/tracking/tracking.config';
import * as Styled from './styled';

function Welcome() {
  const { setLayout } = useLayout();
  const transshipment = useTransshipment();

  const isRepresentative = transshipment.type === 'representative';

  const trackNca = useMemo(() => {
    if (!transshipment.id) return null;

    const sdk = makeRepresentativeTrackingSDK(transshipment.id);

    return sdk?.track ? makeOnboardingNcaTrack(sdk) : null;
  }, [transshipment.id]);

  const takeRate =
    transshipment.type === 'representative'
      ? transshipment.data.take_rate
      : undefined;

  const takeTerms =
    transshipment.type === 'representative'
      ? transshipment.data.take_terms
      : undefined;

  const { runAsync: authorizeTake, loading: authorizing } = useRequest(
    Representative.authorizeTake,
    { manual: true }
  );

  const showIdentityItem = useMemo(() => {
    if (transshipment.data.status === 'retake-documents') {
      return transshipment.data.documents.some(
        (doc) => doc.category === 'identity' && doc.status === 'invalid'
      );
    }

    if (
      transshipment.data.documents === undefined ||
      transshipment.data.documents.length === 0
    )
      return true;

    const identityDocs = transshipment.data.documents.filter(
      (doc) => doc.category === 'identity'
    );

    if (identityDocs.length === 1 && identityDocs[0].type === 'rg-front')
      return true;
  }, []);

  const showSelfieItem = useMemo(() => {
    if (transshipment.data.status === 'retake-documents') {
      return transshipment.data.documents.some(
        (doc) => doc.category === 'selfie' && doc.status === 'invalid'
      );
    }

    if (
      transshipment.data.documents === undefined ||
      transshipment.data.documents.length === 0
    )
      return true;

    return !transshipment.data.documents.some(
      (doc) => doc.category === 'selfie'
    );
  }, []);

  useEffect(() => {
    setLayout({
      header: { position: 'left' },
    });
  }, [setLayout]);

  useEffect(() => {
    if (takeRate) {
      trackNca?.autorizacaoVisualizada(
        takeRate.type === 'PERCENTAGE'
          ? { percentualExibido: takeRate.amount }
          : {}
      );
    }
  }, [takeRate, trackNca]);

  const handleSubmit = async () => {
    if (takeTerms) {
      await authorizeTake(transshipment.id);
      trackNca?.autorizacaoConfirmada();
    }

    transshipment.nextStep();
  };

  return (
    <Form
      initialValues={{ takeAuthorization: true }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ isValid }) => (
        <Styled.Container role="region">
          {transshipment.data.status === 'retake-documents' ? (
            <>
              <Styled.Greeting aria-label={`Olá, ${transshipment.data.name}`}>
                Olá,&nbsp;
                <Styled.Name>{transshipment.data.name}</Styled.Name>
              </Styled.Greeting>
              <Styled.Description>
                Por motivos de segurança, precisamos que você refaça o envio dos
                documentos indicados abaixo.
              </Styled.Description>
            </>
          ) : (
            <>
              {transshipment.type === 'representative' && (
                <>
                  <Styled.Greeting
                    aria-label={`Olá, ${transshipment.data.name}!`}
                  >
                    Olá,&nbsp;
                    <Styled.Name>{transshipment.data.name}</Styled.Name>!
                  </Styled.Greeting>
                  <Styled.Description>
                    Para concluir o cadastro como avalista da operação de
                    crédito da Conta PJ Fretebras, vamos precisar de alguns
                    dados pessoais.
                  </Styled.Description>
                </>
              )}

              {transshipment.type === 'spouse' && (
                <>
                  <Title>Envio de informações</Title>
                  <Styled.Description>
                    Para completar a solicitação de{' '}
                    {transshipment.data.representative}, precisamos da foto do
                    seu documento de identidade e de uma foto do seu rosto para
                    validação.
                  </Styled.Description>
                </>
              )}
            </>
          )}

          <Styled.OutlinedCard data-testid="documents-card">
            {!isRepresentative && <UsingCellphone />}
            <Styled.List>
              {!isRepresentative && (
                <Styled.ListTitle color="neutral" variant="sub300">
                  Tenha em mãos os seguintes documentos:
                </Styled.ListTitle>
              )}

              {showIdentityItem && (
                <Styled.ListItemGroup>
                  <Styled.User />
                  <Styled.ListItem>
                    Documento de identificação (RG ou CNH)
                  </Styled.ListItem>
                </Styled.ListItemGroup>
              )}

              {showSelfieItem && (
                <Styled.ListItemGroup>
                  <Styled.UserFrame />
                  <Styled.ListItem>
                    Também vamos pedir que você tire uma foto do seu rosto
                    (selfie)
                  </Styled.ListItem>
                </Styled.ListItemGroup>
              )}
            </Styled.List>
          </Styled.OutlinedCard>

          {takeRate && takeTerms && (
            <TakeAuthorization takeRate={takeRate} takeTerms={takeTerms} />
          )}

          <Footer
            marginTop="auto"
            withDivider={false}
            havePaddingBottom
            rightButton={
              <ButtonWithArrowRight
                width="100%"
                variant="primary"
                type="submit"
                disabled={!isValid || authorizing}
                loading={authorizing}
              >
                Continuar
              </ButtonWithArrowRight>
            }
          />
        </Styled.Container>
      )}
    </Form>
  );
}

export default Welcome;
